import React, { useEffect, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import { useCallback } from "react";
import FLFRogoDevSubTypeControlPanel from "../../components/ControlPanel/FLFRogoDevSubTypeControlPanel";
import useControlPanel from "src/hooks/useControlPanel";
import { useMemo } from "react";
import Pagination from "src/components/Pagination/Pagination";
import { dataStateType } from "src/constants/status";
import RogoDevSubTypeTable from "../../components/Table/RogoDevSubTypeTable";
import useCurrentActionCache from "src/hooks/useCurrentActionCache";
import {
  createFLFRogoDevSubType,
  getFLFRogoDevSubType,
  removeFLFRogoDevSubType,
  sortFLFRogoDevSubType,
  updateFLFRogoDevSubType,
} from "src/apis/FLF/rogoDevSubType.api";
import { CACHE_ROGO_DEV_SUB_TYPE } from "src/constants/actionCache";
import { compareTwoArrayDnD } from "src/utils/helper";
import { NotifyConfirm } from "src/components/notify/Notify";
import { path } from "src/constants/path";

const initRogoDevSubTypeData = {
  list: [],
  total_record: 0,
};

const FLFRogoDevSubType = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();
  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();
  const [rogoDevSubTypeData, setRogoDevSubTypeData] = useState(
    initRogoDevSubTypeData
  );
  const [filterRogoDevSubTypes, setFilterRogoDevSubTypes] = useState([]);
  const [listRogoDevSubTypeId, setListRogoDevSubTypeId] = useState([]);
  const [queueSortApi, setQueueSortApi] = useState([]);
  const [isSorted, setIsSorted] = useState(false);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [errorsForm, setErrorsForm] = useState({});

  const fetchRogoDevSubType = useCallback(() => {
    getFLFRogoDevSubType({}, (result) => {
      setRogoDevSubTypeData({ ...result });
      setFilterRogoDevSubTypes([...result.list]);
      setListRogoDevSubTypeId([...result.list?.map((c) => c.id)]);
    });
  }, []);

  useEffect(() => {
    let isCancelled = false;
    if (!isCancelled) {
      fetchRogoDevSubType();
    }

    return () => {
      // cancel the request before component unmounts
      isCancelled = true;
    };
  }, [fetchRogoDevSubType]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const handleOpenPopup = useCallback((isOpen, item = null) => {
    setOpenPopup(isOpen);
    setRecordForEdit(item);
    setErrorsForm({});
  }, []);

  const updateFilteredRogoDevSubType = useCallback((rogoDevSubTypes) => {
    setFilterRogoDevSubTypes(rogoDevSubTypes);
    setListRogoDevSubTypeId(
      rogoDevSubTypes.map((rogoDevSubType) => rogoDevSubType.id)
    );
  }, []);

  /* ---- handle search ----- */
  const searchByKeyWord = useCallback(
    (rogoDevSubTypes, key) => {
      if (status === dataStateType.INACTIVE) {
        return [];
      }
      return rogoDevSubTypes.filter((rogoDevSubType) =>
        rogoDevSubType.name.toLowerCase().includes(key.toLowerCase().trim())
      );
    },
    [status]
  );

  const handleSearch = useCallback(
    (value) => {
      const filteredRogoDevSubType = value
        ? searchByKeyWord(rogoDevSubTypeData.list, value)
        : rogoDevSubTypeData.list;
      updateFilteredRogoDevSubType(filteredRogoDevSubType);
    },
    [rogoDevSubTypeData.list, searchByKeyWord, updateFilteredRogoDevSubType]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  /* ---- handle add new and update ----- */
  const addOrEdit = useCallback(
    (rogoDevSubType, resetForm) => {
      if (rogoDevSubType.id) {
        updateFLFRogoDevSubType(
          rogoDevSubType.id,
          rogoDevSubType,
          handleApiResponse
        );
      } else {
        createFLFRogoDevSubType(rogoDevSubType, handleApiResponse);
      }

      function handleApiResponse(result) {
        if (result.data?.status === 422) {
          setErrorsForm(result.data.data);
        } else {
          fetchRogoDevSubType();
          resetForm();
          setRecordForEdit(null);
          setOpenPopup(false);
          setErrorsForm({});
          setCurrentActionCache(CACHE_ROGO_DEV_SUB_TYPE);
        }
      }
    },
    [fetchRogoDevSubType, setCurrentActionCache]
  );

  /* ---- handle sort and DnD ----- */

  const handleSort = useCallback(() => {
    sortFLFRogoDevSubType({ queueSort: queueSortApi }, () => {
      setQueueSortApi([]);
      setIsSorted(false);
      setCurrentActionCache(CACHE_ROGO_DEV_SUB_TYPE);
      fetchRogoDevSubType();
    });
  }, [fetchRogoDevSubType, queueSortApi, setCurrentActionCache]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // kiểm tra nếu không tồn tại over (kéo quá size thì return tránh lỗi)
    if (!over) return;

    if (active.id !== over.id) {
      // lấy vị trí cũ (từ thằng active)
      const oldIndex = filterRogoDevSubTypes.findIndex(
        (rogoDev) => rogoDev.id === active.id
      );

      // lấy vị trí mới (từ thằng order)
      const newIndex = filterRogoDevSubTypes.findIndex(
        (rogoDev) => rogoDev.id === over.id
      );

      const newOrder = arrayMove(filterRogoDevSubTypes, oldIndex, newIndex);
      const newOrderIds = newOrder.map((c) => c.id);

      // check xem có thay đổi thì hiện nút xác nhận cập nhật
      const isOrderChanged = !compareTwoArrayDnD(
        listRogoDevSubTypeId,
        newOrderIds
      );
      setFilterRogoDevSubTypes([...newOrder]);

      if (isOrderChanged) {
        setIsSorted(true);
        setQueueSortApi((prev) => [
          ...prev,
          {
            activeRogoDevSubTypeId: active.id,
            overRogoDevSubTypeId: over.id,
          },
        ]);
      } else {
        setIsSorted(false);
        setQueueSortApi([]);
      }
    }
  };

  const handleDelete = (rogoDevSubTypeDelete) => {
    NotifyConfirm("warning", "Are you delete it ?", (result) => {
      if (result.isConfirmed) {
        removeFLFRogoDevSubType(
          rogoDevSubTypeDelete.id,
          setCurrentActionCache(CACHE_ROGO_DEV_SUB_TYPE)
        );
        setRogoDevSubTypeData((prev) => {
          return {
            ...prev,
            list: prev.list.filter(
              (rogoDevSubType) => rogoDevSubType.id !== rogoDevSubTypeDelete.id
            ),
            total_record: prev.total_record - 1,
          };
        });

        updateFilteredRogoDevSubType((prev) =>
          prev.filter(
            (rogoDevSubType) => rogoDevSubType.id !== rogoDevSubTypeDelete.id
          )
        );
      }
    });
  };

  const resultFilter = useMemo(
    () => searchByKeyWord(filterRogoDevSubTypes, searchKey),
    [filterRogoDevSubTypes, searchByKeyWord, searchKey]
  );
  const listItems = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>Rogo Dev Sub Type</h2>
      </div>

      {/** control pan */}
      <FLFRogoDevSubTypeControlPanel
        handleOpenPopup={handleOpenPopup}
        isSorted={isSorted}
        handleSort={handleSort}
        currentActionCache={currentActionCache}
        recordForEdit={recordForEdit}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        addOrEdit={addOrEdit}
        errorsForm={errorsForm}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={false}
        />
      </FLFRogoDevSubTypeControlPanel>

      {/** table */}
      <RogoDevSubTypeTable
        data={listItems}
        handleOpenPopup={handleOpenPopup}
        handleDelete={handleDelete}
        status={dataStateType.ACTIVE}
        onDragEnd={handleDragEnd}
      />

      {/** Pagination */}
      <Pagination
        pathname={path.flfRogoDevSubType}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default FLFRogoDevSubType;
